import { Icon } from '@iconify/react'
import { useState } from 'react'
import UserType from 'src/model/user-types'
import Badge from 'src/components/user-access/components/badge'
import UpgradePopover from 'src/components/upgrade-popover/upgrade-popover'

const DropdownBox = ({
  box,
  selectedFeatureIndex,
  classes,
  handleClose,
  handleDragStart,
  handleDoubleClick,
  editorRef,
}) => {
  const userControl = UserType[window.CONSTANTS.USER_ROLE_ID]
  const isBasicUser =
    userControl === 'Basic' || UserType.isTrialEquivalent(userControl)


  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedOption, setSelectedOption] = useState(null)

  // Account types that HAVE access to Community Board
  const communityAccessTypes = [
    'Pro',
    'Elite',
    'AppSumo (Tier 5)',
    'AppSumo (Tier 6)',
    'AppSumo (Tier 7)',
    'AppSumo (Tier 8)',
    'AppSumo (Tier 9)',
    'Plus',
    'Pro v2',
    'Launch',
    'Essentials',
    'Growth',
    'Scale',
    'Enterprise',
  ]
  const hasCommunityAccess =
    communityAccessTypes.includes(userControl) ||
    UserType.isTrialEquivalent(userControl)

  const isDisabledForBasicUser = option => {
    const disabledOptions = [
      'fitButton',
      'fullButton',
      'socialButton',
      'buttonOnTheRight',
    ]
    const disabledNames = ['Image with buttons']
    return (
      isBasicUser &&
      (disabledOptions.includes(option.value) ||
        disabledNames.includes(option.name))
    )
  }

  const showCommunityProBadge = option => {
    return option.value === 'communityBlock' && !hasCommunityAccess
  }

  const isComingSoon = option => {
    // Show "Soon" badge for these activities for all users
    const comingSoonActivities = []

    return comingSoonActivities.includes(option.value)
  }

  // Blocks that only make sense once per page. A second one would either duplicate the
  // same reward panel or run a second identical catalog feed on the same landing page.
  const SINGLETON_BLOCKS = ['badge', 'catalog']

  const isBadgeAlreadyExists = option => {
    if (!SINGLETON_BLOCKS.includes(option.value)) return false
    const editor = editorRef?.current
    if (!editor) return false

    // 1. Check via editor model
    try {
      const root = editor.model.document.getRoot()
      if (root) {
        const range = editor.model.createRangeIn(root)
        for (const value of range.getWalker()) {
          if (value.item.is && value.item.is('element', 'dynamicList') && value.item.getAttribute('type') === option.value) return true
        }
      }
    } catch { /* model check failed, try next */ }

    // 2. Check DOM via editor view
    try {
      const domRoot = editor.editing.view.getDomRoot()
      if (domRoot && domRoot.querySelector(`[data-type="${option.value}"]`)) return true
    } catch { /* view DOM check failed, try next */ }

    // 3. Check DOM via document querySelector
    try {
      const editable = document.querySelector('.ck-editor__editable')
      if (editable && editable.querySelector(`[data-type="${option.value}"]`)) return true
    } catch { /* document check failed, try next */ }

    // 4. Check serialized editor data
    try {
      const data = editor.getData()
      if (data.includes(`data-type="${option.value}"`)) return true
    } catch { /* getData check failed */ }

    return false
  }

  const handleBadgeClick = (event, option) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    setSelectedOption(option)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
    setSelectedOption(null)
  }
  return (
    <div className={classes.dropdownContainer}>
      <div className={classes.dropdownHeader}>
        {box[selectedFeatureIndex].value === 'embed' ? (
          <span>Add Embeddable Block</span>
        ) : (
          <span>Add {box[selectedFeatureIndex].category} Block</span>
        )}
        <Icon
          onClick={handleClose}
          className={classes.icon}
          width="16"
          height="16"
          icon="ic:round-close"
        />
      </div>
      {box[selectedFeatureIndex].value === 'embed' ? (
        <div className={classes.optionsGrid}>
          {box[selectedFeatureIndex].options.map((option, index) => (
            <div
              className={`${classes.containerEmbed} ${index < box[selectedFeatureIndex].options.length - 2
                ? classes.withDividerEmbed
                : ''
                }`}
              key={index}
            >
              <div
                draggable
                onDragStart={handleDragStart(option)}
                onDoubleClick={() => handleDoubleClick(option)}
                className={classes.optionEmbedContainer}
              >
                {option.heading && (
                  <div className={classes.optionHeading}>{option.heading}</div>
                )}
                <div className={classes.optionText}>
                  <img
                    className={classes.imageEmbed}
                    src={option.image}
                    alt=""
                  />
                </div>
              </div>
              <div className={classes.optionTitleEmbed}>{option.name}</div>
            </div>
          ))}
        </div>
      ) : box[selectedFeatureIndex].subcategories ? (
        <div className={classes.subcategoriesContainer}>
          {box[selectedFeatureIndex].subcategories.map(
            (subcategory, subIndex) => (
              <div key={subIndex} className={classes.subcategorySection}>
                <div className={classes.subcategoryHeader}>
                  <span
                    className={classes.subcategoryIndicator}
                    style={{ backgroundColor: subcategory.color || '#015FFB' }}
                  />
                  {subcategory.name}
                </div>
                <div
                  className={classes.subcategoryOptionsGrid}
                  style={
                    subcategory.options.length === 1
                      ? { gridTemplateColumns: '1fr' }
                      : {}
                  }
                >
                  {subcategory.options.map((option, optIndex) => {
                    const isDisabled = isDisabledForBasicUser(option)
                    const isSingle = subcategory.options.length === 1
                    const showComingSoon = isComingSoon(option)
                    const isOptionDisabled = isDisabled || showComingSoon

                    return (
                      <div
                        className={`${classes.containerEmbed} ${isOptionDisabled ? classes.disabledContainer : ''
                          }`}
                        key={optIndex}
                        style={isSingle ? { width: '100%' } : {}}
                      >
                        <div
                          draggable={!isOptionDisabled}
                          onDragStart={
                            !isOptionDisabled
                              ? handleDragStart(option)
                              : undefined
                          }
                          onDoubleClick={
                            !isOptionDisabled
                              ? () => handleDoubleClick(option)
                              : undefined
                          }
                          className={
                            isDisabled
                              ? classes.optionContainerDisabled
                              : showComingSoon
                                ? classes.optionContainerSoon
                                : classes.optionEmbedContainer
                          }
                          style={
                            isSingle ? { width: '186px', height: '92px' } : {}
                          }
                        >
                          <div className={classes.optionText}>
                            <img
                              className={classes.imageEmbed}
                              src={option.image}
                              alt=""
                              draggable={false}
                              style={
                                isSingle
                                  ? {
                                    width: 'auto',
                                    height: '100%',
                                    maxWidth: '100%',
                                    objectFit: 'contain',
                                  }
                                  : {}
                              }
                            />
                          </div>
                          {isDisabled && (
                            <div
                              onClick={e => handleBadgeClick(e, option)}
                              className={classes.proBadgeContainer}
                            >
                              <div style={{ display: 'flex', height: '20px', borderRadius: '5px', alignItems: 'center', justifyContent: 'center', backgroundColor: '#015ffb14', padding: '0 8px' }}>
                                <span style={{ fontSize: '11px', fontWeight: 500, color: '#015FFB' }}>Plus</span>
                              </div>
                            </div>
                          )}
                          {showComingSoon && (
                            <div className={classes.proBadgeContainer}>
                              <Badge type={5} />
                            </div>
                          )}
                        </div>
                        <div className={classes.optionTitleEmbed}>
                          {option.name}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ),
          )}
        </div>
      ) : (
        <div>
          {box[selectedFeatureIndex].options.map((option, index) => {
            const isBasicDisabled = isDisabledForBasicUser(option)
            const isBadgeExists = isBadgeAlreadyExists(option)
            const isDisabled = isBasicDisabled || isBadgeExists
            return (
              <div
                className={`${classes.container} ${index < box[selectedFeatureIndex].options.length - 1
                  ? classes.withDivider
                  : ''
                  } ${isDisabled ? classes.disabledContainer : ''}`}
                key={index}
              >
                <div
                  draggable={!isDisabled}
                  onDragStart={
                    !isDisabled ? handleDragStart(option) : undefined
                  }
                  onDoubleClick={
                    !isDisabled ? () => handleDoubleClick(option) : undefined
                  }
                  className={
                    isDisabled
                      ? classes.optionContainerDisabled
                      : classes.optionContainer
                  }
                >
                  {option.heading && (
                    <div className={classes.optionHeading}>
                      {option.heading}
                    </div>
                  )}
                  <div className={classes.optionText}>
                    <img
                      className={classes.image}
                      src={option.image}
                      alt=""
                      draggable={false}
                    />
                  </div>
                  {isBasicDisabled && (
                    <div
                      onClick={e => handleBadgeClick(e, option)}
                      className={classes.proBadgeContainer}
                    >
                      <div style={{ display: 'flex', height: '20px', borderRadius: '5px', alignItems: 'center', justifyContent: 'center', backgroundColor: '#015ffb14', padding: '0 8px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 500, color: '#015FFB' }}>Plus</span>
                      </div>
                    </div>
                  )}
                  {showCommunityProBadge(option) && (
                    <div className={classes.proBadgeContainer}>
                      <Badge type={1} />
                    </div>
                  )}
                </div>
                <div className={classes.optionTitle}>{option.name}</div>
              </div>
            )
          })}
        </div>
      )}
      <UpgradePopover
        id="dropdown-option-upgrade"
        anchorEl={anchorEl}
        handleClose={handlePopoverClose}
        open={Boolean(anchorEl)}
        header="Upgrade to Plus"
        content="Unlock advanced button features including fit buttons, full-width buttons, and social media buttons to enhance your courses."
        accessRuleEqual="editor_buttons"
      />
    </div>
  )
}

export default DropdownBox
